import { AuditStatus } from '@arhs-fleetback/types/entities/audits';
import { generatePath } from 'react-router';
import { all, takeLatest, put, call, fork, select, takeEvery, debounce } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { PATH } from 'constants/RoutesConstants';

import { simpleRequestsLoader } from 'modules/app/utils';
import { getById } from 'modules/auditModels/selectors/general';

import * as dashboardActions from 'modules/audits/actions/dashboard';
import * as detailsActions from 'modules/audits/actions/details';
import * as actionTypes from 'modules/audits/actionTypes';
import { navigate } from 'modules/router/navigate';
import { createNotification } from 'modules/toast-notifications/actions';

import dashboardSagas from './dashboard';
import detailsSagas from './details';
import { navigateToAudits } from '../../../actions/navigateActions';
import { DETAILS_SOURCE } from '../constants';

// Requests handled declaratively via simpleRequest: a plain call → success/failure action + toast.
// Any extra side effect (edit mode on START, dashboard redirect on status change) is a separate
// reducer/matcher reaction, not part of the request itself.
const simpleRequests = {
  [actionTypes.START_REQUEST]: {
    callbacks: {
      success: (audit, meta) => detailsActions.startAuditSuccess(audit, meta),
      failure: (error, meta, { payload }) =>
        detailsActions.startAuditFailure(error, { auditId: payload.auditId, ...meta }),
    },
    notifications: {
      success: { id: 'audito.page.audit.confirmation.start.success', defaultMessage: 'Audit started' },
      failure: { id: 'audito.page.audit.confirmation.start.failure', defaultMessage: 'Unable to start the audit' },
    },
  },
  [actionTypes.PATCH_ASSIGNEES_REQUEST]: {
    callbacks: {
      success: (audit, meta) => detailsActions.patchAssigneesSuccess(audit, meta),
      failure: (error, meta) => detailsActions.patchAssigneesFailure(error, meta),
    },
    notifications: {
      success: { id: 'audito.page.audit.notification.assign.success', defaultMessage: 'The audit file assignee has been updated with success.' },
      failure: { id: 'audito.page.audit.notification.assign.failure', defaultMessage: 'Unable to update the assignee' },
    },
  },
  [actionTypes.PATCH_START_DATE_REQUEST]: {
    callbacks: {
      success: (audit, meta) => detailsActions.patchStartDateSuccess(audit, meta),
      failure: (error, meta) => detailsActions.patchStartDateFailure(error, meta),
    },
    notifications: {
      success: { id: 'audito.page.audit.notification.start_date.success', defaultMessage: 'Start date updated' },
      failure: { id: 'audito.page.audit.notification.start_date.failure', defaultMessage: 'Unable to update the start date' },
    },
  },
  [actionTypes.PUT_REQUEST]: {
    callbacks: {
      success: audit => detailsActions.putSuccess(audit),
      failure: (error, meta) => detailsActions.putFailure(error, meta),
    },
    notifications: {
      success: 'audito.page.audit.confirmation.save.success',
      failure: 'audito.page.audit.confirmation.save.failure',
    },
  },
  [actionTypes.DELETE_REQUEST]: {
    callbacks: {
      success: (_, meta, { payload }) => detailsActions.deleteSuccess(payload.request.params.auditId, meta),
      failure: (error, meta) => detailsActions.deleteFailure(error, meta),
    },
    notifications: {
      success: 'audito.page.audit.confirmation.delete.success',
      failure: 'audito.page.audit.confirmation.delete.failure',
    },
  },
  [actionTypes.PATCH_STATUS_REQUEST]: {
    callbacks: {
      success: (audit, meta) => detailsActions.patchStatusSuccess(audit, meta),
      failure: (error, meta, { payload }) =>
        detailsActions.patchStatusFailure(error, { auditId: payload.auditId, ...meta }),
    },
    // reopen vs close message depends on the requested status.
    notifications: {
      success: (_, { payload }) =>
        (payload.request.data.status === AuditStatus.OPEN
          ? 'audito.page.audit.confirmation.reopen.success'
          : 'audito.page.audit.confirmation.close.success'),
      failure: (_, { payload }) =>
        (payload.request.data.status === AuditStatus.OPEN
          ? 'audito.page.audit.confirmation.reopen.failure'
          : 'audito.page.audit.confirmation.close.failure'),
    },
  },
};

/** @param {import("modules/audits/types/details").PostAuditRequestAction} action */
function* create({ payload }) {
  let { request } = payload;

  let message = 'audito.page.audit.notification.create_audit.success';
  let type = 'success';

  try {
    // TODO - REMOVE IT, API NEEDS TO HANDLE AUDITMODELID AND NOT "ID" FROM AUDIT MODEL - BMWVID-10076
    const auditModel = yield select(state => getById(state, request.data.auditModelId));
    request = {
      ...request,
      data: {
        ...request.data,
        auditModelId: auditModel?.id ?? request.data.auditModelId,
      },
    };

    const { data: audit } = yield call(restClient.request, request);
    yield put(detailsActions.postSuccess(audit));
    yield call(navigate, generatePath(PATH.AUDITS_DETAILED.path, { auditId: audit.id }), { state: payload.navigateState });
  } catch (errorMessage) {
    yield put(detailsActions.postFailure(errorMessage));
    message = 'audito.page.audit.notification.create_audit.failure';
    type = 'error';
  }

  yield put(createNotification({ type, message }));
}

/** @param {import("modules/audits/types/details").PostCloneAction} action */
function* clone({ payload }) {
  const { request } = payload;

  let message = 'audito.page.audit.confirmation.duplicate.success';
  let type = 'success';

  try {
    const { data: audit } = yield call(restClient.request, request);
    yield put(detailsActions.postCloneSuccess(audit));

    // Redirect to the duplicated audit; selection + fetch are driven by the route (scopeToPage).
    // If auto-editing the clone is later requested, pass it through the navigation state
    // (replace(url, { edit: true })) and read it via getLocationState — no reducer needed.
    const newUrl = generatePath(PATH.AUDITS_DETAILED.path, { auditId: audit.id });
    yield call(navigate, newUrl, { state: payload.navigateState });
  } catch (errorMessage) {
    yield put(detailsActions.postCloneFailure(errorMessage));
    message = 'audito.page.audit.confirmation.duplicate.failure';
    type = 'error';
  }

  yield put(createNotification({ type, message }));
}

function* get({ payload, meta }) {
  const { request } = payload;

  try {
    const { data: audit } = yield call(restClient.request, request);
    yield put(detailsActions.fetchAuditSuccess(audit, meta));
  } catch (errorMessage) {
    // The meta must be forwarded: `ui/details/loading.fetch` only reacts to the DETAILS_SOURCE fetch.
    yield put(detailsActions.fetchAuditFailure(errorMessage, meta));
  }
}

function* getAll({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: audits } = yield call(restClient.request, request);
    yield put(dashboardActions.fetchAuditsSuccess(audits, meta));
  } catch (errorMessage) {
    yield put(dashboardActions.fetchAuditsFailure(errorMessage, meta));
  }
}

function* afterSuccessfulDelete() {
  yield call(navigate, generatePath(PATH.AUDITS.path));
}

// Status change can request a redirect back to the dashboard (e.g. closing from the detail page).
// The PATCH itself is a simpleRequest; the redirect is a separate reaction to its success, same
// pattern as `afterSuccessfulDelete`.
function* redirectToDashboardAfterStatusChange() {
  yield call(navigateToAudits);
}

function* fetchNextPage({ payload, meta }) {
  const params = payload?.params ?? {};
  const page = params.page ?? {};
  yield put(dashboardActions.fetchAudits({ ...params, page: page.number + 1 }, meta));
}

function* fetchSignedNextPage({ payload, meta }) {
  const params = payload?.params ?? {};
  let page = params.page ?? 0;
  yield put(dashboardActions.fetchSignedAudits({ ...params, page: ++page }, meta));
}

const deleteAndRedirectAction = ({ type, meta }) => type === actionTypes.DELETE_SUCCESS && meta?.source === DETAILS_SOURCE;

const statusChangeRedirectAction = ({ type, meta }) =>
  type === actionTypes.PATCH_STATUS_SUCCESS && Boolean(meta?.redirectToDashboard);

const isDebouncedFetchAuditsChannel = ({ type, payload }) => type === actionTypes.FETCH_AUDITS_REQUEST && payload.request.params.page === 0;

const isDirectFetchAuditsChannel = action => action.type === actionTypes.FETCH_AUDITS_REQUEST && !isDebouncedFetchAuditsChannel(action);

export default function* auditSagas() {
  yield fork(detailsSagas);
  yield fork(dashboardSagas);
  yield all(simpleRequestsLoader(simpleRequests));
  yield takeLatest(actionTypes.POST_REQUEST, create);
  yield takeLatest(actionTypes.POST_CLONE_REQUEST, clone);
  yield takeLatest(actionTypes.FETCH_AUDIT_REQUEST, get);
  yield takeLatest(actionTypes.GET_NEXT_PAGE, fetchNextPage);
  yield takeLatest(actionTypes.GET_SIGNED_NEXT_PAGE, fetchSignedNextPage);

  // Search audit cases
  yield takeLatest(isDirectFetchAuditsChannel, getAll);
  yield debounce(500, isDebouncedFetchAuditsChannel, getAll);

  // Redirections
  yield takeEvery(deleteAndRedirectAction, afterSuccessfulDelete);
  yield takeEvery(statusChangeRedirectAction, redirectToDashboardAfterStatusChange);
}
